import React, { useMemo } from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';

import DataTable from '../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon, {
  resolveAttributionMethod,
} from '../../../../../../shared/designSystem/components/AttributionIcon';
import { formatWholeNumber } from '../../../../../../../core/utils/campaigns';
import ImpressionSubTable from './ImpressionsSubTable';

// =============================================================================
// PageViewsTable — V1 facelift to the shared v2 DataTable primitive.
// Same column model as before: Page URL (with "Visit Page" link), Page View
// Time, Impressions, First Impression, Attribution Method. Attribution PNG
// sprites + fallback parsing collapse into the shared `<AttributionIcon>` +
// `resolveAttributionMethod` helper. Expanded rows render the impression
// timeline via the shared `<ImpressionTimeline>` adapter.
// =============================================================================

const isValidUrl = (urlString) => {
  const urlPattern = new RegExp(
    '^(https?:\\/\\/)?' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i'
  );
  return !!urlPattern.test(urlString);
};

const addProtocol = (url) => {
  if (!/^(\w+:)?\/\//i.test(url)) return `https://${url}`;
  return url;
};

const resolveVisitUrl = (text) => {
  if (isValidUrl(text)) return text;
  const split = (text || '').split('//');
  if (split.length > 1 && split[0] !== 'http:' && split[0] !== 'https:') {
    return addProtocol(split[1]);
  }
  return text;
};

// Split a raw URL string into host + path-with-query for the two-line cell
// render. Falls back to a manual split when `new URL()` rejects malformed
// strings — analytics data sometimes carries already-stripped hostnames or
// non-URL identifiers in the page-url field.
const parsePageUrl = (raw) => {
  if (!raw) return { host: '', path: '' };
  try {
    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(raw);
    const u = new URL(hasScheme ? raw : `https://${raw}`);
    const path = `${u.pathname || '/'}${u.search || ''}${u.hash || ''}`;
    return { host: u.host, path };
  } catch (e) {
    const stripped = raw.replace(/^https?:\/\//, '');
    const slashIdx = stripped.indexOf('/');
    if (slashIdx === -1) return { host: stripped, path: '' };
    return {
      host: stripped.slice(0, slashIdx),
      path: stripped.slice(slashIdx),
    };
  }
};

const PageViewsTable = ({ data }) => {
  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return [...data].sort((a, b) =>
      moment(b.pageViewTime).diff(moment(a.pageViewTime))
    );
  }, [data]);

  const columns = useMemo(
    () => [
      {
        id: 'pageUrl',
        accessorKey: 'pageUrl',
        header: 'Page URL',
        sortable: true,
        meta: { align: 'left', width: 240 },
        cell: ({ getValue }) => {
          const text = getValue() || '';
          const { host, path } = parsePageUrl(text);
          const url = resolveVisitUrl(text);
          return (
            <Tooltip overlayClassName="mjl-ds" title={text || '—'}>
              <div className="campaign-page-view-v1__url-cell">
                {host && (
                  <span className="campaign-page-view-v1__url-host">{host}</span>
                )}
                <span className="campaign-page-view-v1__url-text">
                  {path || host || '—'}
                </span>
                <a
                  className="campaign-page-view-v1__visit-link"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-no-expand
                >
                  Visit Page
                </a>
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: 'pageViewTime',
        accessorKey: 'pageViewTime',
        header: 'Page View Time',
        sortable: true,
        sortingFn: (a, b) =>
          moment(a.original.pageViewTime).unix() - moment(b.original.pageViewTime).unix(),
        meta: { align: 'left', width: 180 },
        cell: ({ getValue }) => getValue() || '—',
      },
      {
        id: 'totalImpressions',
        accessorKey: 'totalImpressions',
        header: 'Impressions',
        sortable: true,
        sortingFn: (a, b) =>
          Number(a.original.totalImpressions || 0) - Number(b.original.totalImpressions || 0),
        meta: { align: 'right', width: 130 },
        cell: ({ getValue }) => formatWholeNumber(getValue() || 0),
      },
      {
        id: 'firstImpressionTime',
        accessorKey: 'firstImpressionTime',
        header: 'First Impression',
        sortable: true,
        sortingFn: (a, b) =>
          moment(a.original.firstImpressionTime).unix() -
          moment(b.original.firstImpressionTime).unix(),
        meta: { align: 'left', width: 180 },
        cell: ({ getValue }) => getValue() || '—',
      },
      {
        id: 'attributionMethod',
        accessorKey: 'attributionMethod',
        header: '',
        sortable: false,
        meta: { align: 'center', width: 70 },
        cell: ({ getValue }) => {
          const method = resolveAttributionMethod(getValue());
          return method ? <AttributionIcon method={method} /> : null;
        },
      },
    ],
    []
  );

  // Mobile card — stacked layout for narrow viewports. Header: truncated
  // Page URL + AttributionIcon. Sub-line: Page View Time (timestamp).
  // Metric chips: Impressions + First Impression. Visit Page link at the
  // bottom — `data-no-expand` keeps a tap on the link from also toggling
  // the expand row.
  const renderMobileCard = (record) => {
    const text = record.pageUrl || '';
    const { host, path } = parsePageUrl(text);
    const url = resolveVisitUrl(text);
    const method = resolveAttributionMethod(record.attributionMethod);
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <Tooltip overlayClassName="mjl-ds" title={text || '—'}>
            <div className="campaign-page-view-v1__url-cell campaign-page-view-v1__url-cell--card">
              {host && (
                <span className="campaign-page-view-v1__url-host">{host}</span>
              )}
              <span className="campaign-page-view-v1__url-text">
                {path || host || '—'}
              </span>
            </div>
          </Tooltip>
          {method && <AttributionIcon method={method} />}
        </div>

        <div className="campaign-overview__lineitem-card-meta">
          <span className="campaign-overview__lineitem-flight">
            {record.pageViewTime || '—'}
          </span>
        </div>

        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {formatWholeNumber(record.totalImpressions || 0)}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">
              First impression
            </span>
            <span
              className="campaign-overview__creatives-card-metric-value"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              {record.firstImpressionTime || '—'}
            </span>
          </div>
        </div>

        {text && (
          <a
            className="campaign-page-view-v1__visit-link campaign-overview__creatives-card-link"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-no-expand
          >
            Visit Page
          </a>
        )}
      </>
    );
  };

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">Page Views</div>
      </header>
      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={rows}
            columns={columns}
            pageSize={10}
            pageSizeOptions={[10, 20, 50, 100]}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            expandable={{
              isRowExpandable: (record) => (record.impressions || []).length > 0,
              renderExpandedRow: (record) => (
                <ImpressionSubTable impressions={record.impressions || []} />
              ),
            }}
            emptyState="No page views to display."
          />
        </div>
      </div>
    </section>
  );
};

export default PageViewsTable;
